/**
 * MSAL v5 popup sign-in/sign-out completes only when the page the popup is
 * redirected back to hands the auth response to the opener through MSAL's
 * redirect bridge. The Entra ID app registration's redirect URIs are the bare
 * admin origins, so that page is this app: index.js must relay the response
 * and skip booting the app, otherwise routing and the Cognito session restore
 * would navigate away and the opener would wait until popupBridgeTimeout.
 */

/** MSAL joins its library state and any caller-supplied state with "|". */
const MSAL_STATE_DELIMITER = '|'

function decodeBase64Url(value: string): string {
  return atob(value.replace(/-/g, '+').replace(/_/g, '/'))
}

function isMsalLibraryState(state: string): boolean {
  try {
    const [libraryState] = state.split(MSAL_STATE_DELIMITER)
    const parsed = JSON.parse(decodeBase64Url(libraryState))
    return (
      typeof parsed?.id === 'string' &&
      typeof parsed?.meta?.interactionType === 'string'
    )
  } catch {
    return false
  }
}

/**
 * True when the current URL carries an MSAL auth response (in the hash or the
 * query string), i.e. this window is an MSAL popup returning from Microsoft.
 */
export function isMsalRedirectResponse(
  location: Pick<Location, 'hash' | 'search'> = window.location
): boolean {
  return [location.hash, location.search].some((part) => {
    const state = new URLSearchParams(part.slice(1)).get('state')
    return state ? isMsalLibraryState(state) : false
  })
}

/** Hands the auth response in the URL to the window that opened the popup. */
export async function relayMsalRedirectResponse(): Promise<void> {
  const { broadcastResponseToMainFrame } = await import(
    '@azure/msal-browser/redirect-bridge'
  )
  await broadcastResponseToMainFrame()
}
